import { computed, getCurrentScope, onScopeDispose, ref, shallowRef, watch, type Ref } from 'vue';
import {
  deleteGeneratedRecordAttachment,
  deletePendingGeneratedRecordAttachment,
  deletePendingGeneratedRecordVisit,
  deletePendingGeneratedRecordVisitOnUnload,
  GENERATED_RECORD_LOCK_CHANGED_EVENT,
  getGeneratedRecordAttachmentFile,
  getGeneratedRecordForm,
  isGeneratedRecordLockEvent,
  isPendingAttachmentsUnavailable,
  uploadGeneratedRecordAttachment,
  uploadPendingGeneratedRecordAttachment,
  type GeneratedRecordAttachment,
  type GeneratedRecordForm,
} from '@/entities/GeneratedRecord';
import { useAuthStore } from '@/entities/Session';
import { onRealtimeEvent } from '@/shared/api';
import {
  downloadBlobFile,
  extractApiErrorMessage,
  extractAxiosBlobErrorMessage,
  useCustomToast,
} from '@/shared/lib';
import { opensInBrowser, screenAttachmentSelection } from '../lib/record-attachment-rules';
import { createVisitId } from '../lib/visit-id';

export const ATTACHMENT_SENT_TITLE = 'Anexo enviado com sucesso.';
export const ATTACHMENT_SEND_ERROR_TITLE = 'Erro ao enviar anexo. Tente novamente.';
export const ATTACHMENT_DELETED_TITLE = 'Anexo deletado com sucesso.';
export const ATTACHMENT_DELETE_ERROR_TITLE = 'Erro ao deletar anexo. Tente novamente.';
export const ATTACHMENT_OPEN_ERROR_TITLE = 'Erro ao abrir anexo. Tente novamente.';

export type RecordAttachmentsMode = 'generate' | 'fill' | 'edit' | 'view';

export interface RecordAttachmentRow {
  key: string;
  fileName: string;
  sending: boolean;
  deleting: boolean;
  canDelete: boolean;
}

interface StoredAttachment {
  item: GeneratedRecordAttachment;
  // The pending file of the generation screen, opened from the browser: it has no record to read from.
  file: File | null;
}

interface OwnWrite {
  version: number;
  apply: (list: StoredAttachment[]) => StoredAttachment[];
  sentId?: string;
}

interface SendingFile {
  key: string;
  file: File;
}

export interface PendingAttachmentsSave {
  link: { attachmentIds: string[]; visitId: string } | Record<string, never>;
  linked: () => void;
  failed: (error: unknown) => void;
}

const NOTHING_TO_LINK: PendingAttachmentsSave = {
  link: {},
  linked: () => undefined,
  failed: () => undefined,
};

interface RecordAttachmentsOptions {
  mode: RecordAttachmentsMode;
  form: Ref<GeneratedRecordForm | null>;
  lockToken?: () => string | null;
}

export function useRecordAttachments({ mode, form, lockToken }: RecordAttachmentsOptions) {
  const { showSuccessToast, showErrorToast } = useCustomToast();
  const stored = shallowRef<StoredAttachment[]>([]);
  const sendingFiles = ref<SendingFile[]>([]);
  const deletingIds = ref<string[]>([]);
  const isPending = mode === 'generate';
  let sendingSeq = 0;
  let queue: Promise<void> = Promise.resolve();
  const leftSends = new Set<Promise<boolean>>();
  // Bumped when the screen moves to another form or goes away: a send that lands after it belongs to
  // nobody on screen.
  let generation = 0;
  // The last generation a branch switch or a logout ended.
  let sessionEndedThrough = -1;
  // Bumped by every write of the list, so a refresh read before the write cannot bring back a deleted
  // row or drop a new one.
  let listVersion = 0;
  // The writes of this screen since the list was last replaced.
  let ownWrites: OwnWrite[] = [];
  let latestRefresh = 0;
  // A new one for every form the generation screen loads: the server counts the pending limit per
  // visit, the same set this screen lists.
  const visitId = ref('');
  const savingVisit = ref<string | null>(null);
  // A creation is linking the pendings of these visits: discarding one would race the link and could
  // make the save fail. A visit still current when its save fails is discarded later, on leaving or by
  // the refusal of its pendings; only one that ended during the save is left to the hourly cleanup.
  const linkingVisits = new Set<string>();

  const recordId = computed(() => form.value?.record?.id ?? null);
  // While its save is in flight a visit holds only what that save sent: a file attached meanwhile
  // would stay out of the record, and a refusal of the pendings would discard it too.
  const writable = computed(
    () =>
      mode !== 'view' &&
      form.value?.record?.status !== 'ARCHIVED' &&
      !(isPending && savingVisit.value === visitId.value)
  );
  const canAttach = computed(() => writable.value && form.value !== null);
  const busy = computed(() => sendingFiles.value.length > 0 || deletingIds.value.length > 0);

  function savedRecordId(): number {
    const id = recordId.value;
    if (id === null) throw new Error('A saved record form always carries its record.');
    return id;
  }

  function canDelete(item: GeneratedRecordAttachment): boolean {
    if (!writable.value) return false;
    return isPending || item.canDelete;
  }

  const rows = computed<RecordAttachmentRow[]>(() => [
    ...stored.value.map(({ item }) => ({
      key: item.id,
      fileName: item.fileName,
      sending: false,
      deleting: deletingIds.value.includes(item.id),
      canDelete: canDelete(item),
    })),
    ...sendingFiles.value.map(({ key, file }) => ({
      key,
      fileName: file.name,
      sending: true,
      deleting: false,
      canDelete: false,
    })),
  ]);

  // A refresh laid under this upload may already list the file.
  function withStored(list: StoredAttachment[], next: StoredAttachment): StoredAttachment[] {
    const index = list.findIndex(({ item }) => item.id === next.item.id);
    if (index === -1) return [...list, next];
    return list.map((current, position) => (position === index ? next : current));
  }

  function replaceList(items: GeneratedRecordAttachment[]): void {
    listVersion += 1;
    ownWrites = [];
    stored.value = items.map((item) => ({ item, file: null }));
  }

  function writeOwn(apply: OwnWrite['apply'], sentId?: string): void {
    listVersion += 1;
    if (!isPending) ownWrites = [...ownWrites, { version: listVersion, apply, sentId }];
    stored.value = apply(stored.value);
  }

  watch(
    form,
    (loaded) => {
      generation += 1;
      if (isPending) {
        if (loaded === null) return;
        // A form read again (another matrix or another device on a reused screen) starts a blank
        // sheet, so the pendings sent beside the previous one go with it.
        const ended = endVisit();
        if (ended === null) visitId.value = createVisitId();
        else void discardVisit(ended);
        return;
      }
      replaceList(loaded?.attachments ?? []);
    },
    { immediate: true }
  );

  function sendFile(file: File, loaded: GeneratedRecordForm): Promise<GeneratedRecordAttachment> {
    if (isPending) {
      return uploadPendingGeneratedRecordAttachment(loaded.matrix.id, visitId.value, file);
    }
    const token = mode === 'fill' ? (lockToken?.() ?? undefined) : undefined;
    return uploadGeneratedRecordAttachment(savedRecordId(), file, token);
  }

  async function sendOne(entry: SendingFile, sentFor: number): Promise<boolean> {
    const loaded = form.value;
    try {
      if (loaded === null || sentFor !== generation) return false;
      const item = await sendFile(entry.file, loaded);
      if (sentFor !== generation) {
        // After a branch switch or a logout the delete would hit another branch or answer 401 and
        // set off the session-expired handling: the hourly cleanup removes that file instead.
        if (isPending && sentFor > sessionEndedThrough) {
          await deletePendingGeneratedRecordAttachment(item.id).catch(() => undefined);
        }
        return false;
      }
      const sent = { item, file: isPending ? entry.file : null };
      writeOwn((list) => withStored(list, sent), item.id);
      return true;
    } catch (error) {
      if (sentFor === generation) {
        const message = extractApiErrorMessage(error);
        showErrorToast({
          title: ATTACHMENT_SEND_ERROR_TITLE,
          type: 'error',
          ...(message ? { message } : {}),
        });
      }
      return false;
    } finally {
      sendingFiles.value = sendingFiles.value.filter(({ key }) => key !== entry.key);
    }
  }

  async function startSend(entry: SendingFile, sentFor: number): Promise<boolean> {
    const send = sendOne(entry, sentFor);
    leftSends.add(send);
    try {
      return await send;
    } finally {
      leftSends.delete(send);
    }
  }

  async function attach(files: readonly File[]): Promise<void> {
    if (!canAttach.value) return;
    const { accepted, refusals } = screenAttachmentSelection(files, rows.value.length);
    refusals.forEach((title) => showErrorToast({ title, type: 'error' }));
    if (accepted.length === 0) return;

    const entries = accepted.map((file) => ({ key: `sending-${++sendingSeq}`, file }));
    const sentFor = generation;
    sendingFiles.value = [...sendingFiles.value, ...entries];
    let sent = 0;
    for (const entry of entries) {
      const done = queue.then(() => startSend(entry, sentFor));
      queue = done.then(() => undefined);
      if (await done) sent += 1;
    }
    if (sent > 0) showSuccessToast({ title: ATTACHMENT_SENT_TITLE, type: 'success' });
  }

  function findStored(key: string): StoredAttachment | undefined {
    return stored.value.find(({ item }) => item.id === key);
  }

  function deleteStored(key: string): Promise<void> {
    if (isPending) return deletePendingGeneratedRecordAttachment(key);
    const token = mode === 'fill' ? (lockToken?.() ?? undefined) : undefined;
    return deleteGeneratedRecordAttachment(savedRecordId(), key, token);
  }

  async function remove(key: string): Promise<void> {
    const entry = findStored(key);
    if (!entry || !canDelete(entry.item) || deletingIds.value.includes(key)) return;
    deletingIds.value = [...deletingIds.value, key];
    try {
      await deleteStored(key);
      writeOwn((list) => list.filter(({ item }) => item.id !== key));
      showSuccessToast({ title: ATTACHMENT_DELETED_TITLE, type: 'success' });
    } catch (error) {
      const message = extractApiErrorMessage(error);
      showErrorToast({
        title: ATTACHMENT_DELETE_ERROR_TITLE,
        type: 'error',
        ...(message ? { message } : {}),
      });
    } finally {
      deletingIds.value = deletingIds.value.filter((deleting) => deleting !== key);
    }
  }

  async function readFile(entry: StoredAttachment): Promise<Blob> {
    if (entry.file !== null) return entry.file;
    return getGeneratedRecordAttachmentFile(savedRecordId(), entry.item.id);
  }

  // A type the browser can show opens in a tab, which is opened before any await, inside the click,
  // or the popup blocker takes it. With 'noopener' window.open returns null, so the opener is cut by
  // hand instead. Any other type is downloaded with its own name and opens no tab at all.
  async function open(key: string): Promise<void> {
    const entry = findStored(key);
    if (!entry) return;
    const inTab = opensInBrowser(entry.item.fileName);
    const pendingTab = inTab ? window.open('', '_blank') : null;
    if (pendingTab) pendingTab.opener = null;
    try {
      const blob = await readFile(entry);
      if (!inTab) {
        downloadBlobFile(blob, entry.item.fileName);
        return;
      }
      const url = URL.createObjectURL(blob);
      if (pendingTab) pendingTab.location.href = url;
      else window.open(url, '_blank', 'noopener');
      setTimeout(() => URL.revokeObjectURL(url), 100);
    } catch (error) {
      pendingTab?.close();
      const message = await extractAxiosBlobErrorMessage(error, '').catch(() => null);
      showErrorToast({
        title: ATTACHMENT_OPEN_ERROR_TITLE,
        type: 'error',
        ...(message ? { message } : {}),
      });
    }
  }

  // Sends of the visit still in the air delete themselves when they land. Returns the visit that has
  // something to discard on the server, and a new visit starts in its place.
  function endVisit(): string | null {
    generation += 1;
    if (stored.value.length === 0 && sendingFiles.value.length === 0) return null;
    const ended = visitId.value;
    stored.value = [];
    visitId.value = createVisitId();
    return ended;
  }

  // A failed delete never keeps the person on the screen: the hourly cleanup removes pendings older than 24 h.
  function discardVisit(visit: string): Promise<void> {
    if (linkingVisits.has(visit)) return Promise.resolve();
    return deletePendingGeneratedRecordVisit(visit).catch(() => undefined);
  }

  async function dropPending(): Promise<void> {
    const ended = endVisit();
    if (ended !== null) await discardVisit(ended);
  }

  function dropPendingBeforeSessionChange(): Promise<void> {
    sessionEndedThrough = generation;
    return dropPending();
  }

  // Leaving without Cancelar (another route, a reload, the tab closed) takes the pendings of the visit
  // along. The visit starts over, so a page brought back from the browser cache lists nothing it lost.
  function dropPendingOnLeave(): void {
    const ended = endVisit();
    if (ended === null || linkingVisits.has(ended)) return;
    deletePendingGeneratedRecordVisitOnUnload(ended);
  }

  async function discardPending(): Promise<void> {
    if (!isPending) return;
    await dropPending();
  }

  // The ids and the visit are taken when the save leaves: by the time it answers, the screen may have
  // read the form again and listed pendings of a new visit, which this save never linked.
  function beginSave(): PendingAttachmentsSave {
    if (!isPending) return NOTHING_TO_LINK;
    const attachmentIds = stored.value.map(({ item }) => item.id);
    const visit = visitId.value;
    savingVisit.value = visit;
    const settle = () => {
      if (savingVisit.value === visit) savingVisit.value = null;
    };
    if (attachmentIds.length === 0) return { link: {}, linked: settle, failed: settle };
    linkingVisits.add(visit);
    return {
      link: { attachmentIds, visitId: visit },
      // They are the record's now and must not be discarded.
      linked: () => {
        settle();
        linkingVisits.delete(visit);
        stored.value = stored.value.filter(({ item }) => !attachmentIds.includes(item.id));
      },
      // Pendings already gone on the server make the creation refuse them all, and only sending the
      // files again brings them back. The ones still there would count in the limit unseen, so the
      // visit goes with them.
      failed: (error) => {
        settle();
        linkingVisits.delete(visit);
        if (visit === visitId.value && isPendingAttachmentsUnavailable(error)) void dropPending();
      },
    };
  }

  // A read crossed only by writes of this screen is kept, with those writes laid over it again: dropping
  // it would lose someone else's change whose event already came, and reading again would cost an own
  // upload two full form reads. Two crossings are asked once more: a new form, and an own upload the
  // read does not list, which is either not in the read yet or deleted by someone else after it landed;
  // laying it over would bring back a deleted file until the next event.
  // A send that left before the read may already be in it, and its row has no id to pair with until
  // the upload answers: the read waits for those sends, so the answer is laid over the read by id. It
  // never waits for the queue behind them, which would hold a change of someone else for every file.
  async function refresh(retry = true): Promise<void> {
    const id = recordId.value;
    if (id === null) return;
    const readAt = listVersion;
    const request = ++latestRefresh;
    const crossingSends = [...leftSends];
    try {
      const fresh = await getGeneratedRecordForm(id);
      await Promise.all(crossingSends);
      if (request !== latestRefresh || recordId.value !== id) return;
      const items = fresh.attachments ?? [];
      const crossing = ownWrites.filter(({ version }) => version > readAt);
      const laid = crossing.reduce(
        (list, write) => write.apply(list),
        items.map((item): StoredAttachment => ({ item, file: null }))
      );
      const unconfirmed = crossing.some(
        ({ sentId }) =>
          sentId !== undefined &&
          !items.some((item) => item.id === sentId) &&
          laid.some(({ item }) => item.id === sentId)
      );
      if (crossing.length === listVersion - readAt && !(retry && unconfirmed)) {
        replaceList(items);
        stored.value = laid;
      } else if (retry) {
        await refresh(false);
      }
    } catch {
      // The next change of the record, or opening it again, reads the list again.
    }
  }

  if (isPending) {
    window.addEventListener('pagehide', dropPendingOnLeave);
    // The visit is discarded under the branch and the session that sent it, before either changes.
    const unregisterSessionHook = useAuthStore().onBeforeSessionChange(
      dropPendingBeforeSessionChange
    );
    if (getCurrentScope()) {
      onScopeDispose(() => {
        window.removeEventListener('pagehide', dropPendingOnLeave);
        unregisterSessionHook();
        dropPendingOnLeave();
      });
    }
  } else {
    const stopLockEvents = onRealtimeEvent(
      GENERATED_RECORD_LOCK_CHANGED_EVENT,
      (event: unknown) => {
        if (isGeneratedRecordLockEvent(event) && event.generatedRecordId === recordId.value) {
          void refresh();
        }
      }
    );
    if (getCurrentScope()) onScopeDispose(stopLockEvents);
  }

  if (getCurrentScope()) {
    onScopeDispose(() => {
      generation += 1;
    });
  }

  return {
    rows,
    canAttach,
    busy,
    attach,
    open,
    remove,
    beginSave,
    discardPending,
  };
}

export type RecordAttachments = ReturnType<typeof useRecordAttachments>;
